import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, Optional, RendererFactory2 } from '@angular/core';
import { environment } from '@core/const';
import { EnvironmentService } from '@core/feature';
import { YA_CONFIG, YaConfig, Ymaps } from '@yandex-maps/const';
import { BehaviorSubject, Observable, from, fromEvent, merge, throwError } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class YmapsService {
  // инициализация навешена на все поля, чтобы загрузка яндекса осуществлялась по запросу, а не сразу
  // ВАЖНО: если появятся новые публичные поля, проворачивать с ними то же самое
  private _ymaps$: BehaviorSubject<Ymaps> = new BehaviorSubject(undefined);
  private _map$ = new BehaviorSubject<ymaps.Map | null>(null);
  private _isLoaded$ = new BehaviorSubject<boolean>(false);

  private config: YaConfig;
  private script: HTMLScriptElement;
  private window: Window;
  private defaultConfig: YaConfig;
  private isInitialized = false;

  get ymaps$(): Observable<Ymaps> {
    this.init();

    return this._ymaps$.pipe(filter(Boolean)) as Observable<Ymaps>;
  }

  get map$(): BehaviorSubject<ymaps.Map | null> {
    this.init();

    return this._map$;
  }

  get isLoaded$(): BehaviorSubject<boolean> {
    this.init();

    return this._isLoaded$;
  }

  constructor(
    @Optional() @Inject(YA_CONFIG) private yaConfig: YaConfig | null,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
    private environmentService: EnvironmentService,
    private rendererFactory: RendererFactory2,
  ) {}

  private init(): void {
    if (this.environmentService.isPlatformServer || this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    this.defaultConfig = {
      lang: 'ru_RU',
      apikey: environment.yandexMapsApiKey,
    };

    this.config = this.yaConfig || this.defaultConfig;

    if (this.document.defaultView) {
      this.window = this.document.defaultView;
    } else {
      throw new Error('document.defaultView is null');
    }

    if ('ymaps' in this.window) {
      from(ymaps.ready()).pipe(
        take(1),
        map(() => ymaps as Ymaps),
      );
    }

    if (!this.script) {
      const script = this.document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.getScriptSource(this.config);
      script.id = 'yandexMapsApiScript';
      script.defer = true;

      this.script = this.document.body.appendChild(script);
    }

    const load = fromEvent(this.script, 'load').pipe(
      switchMap(() => from(this.ngZone.run(() => ymaps.ready())).pipe(map(() => ymaps as Ymaps))),
    );

    const error = fromEvent(this.script, 'error').pipe(switchMap((e: Event) => throwError(() => e)));

    this.createMap();

    merge(load, error)
      .pipe(take(1), filter(Boolean))
      .subscribe((ymaps: Ymaps) => {
        this.ngZone.run(() => {
          this._ymaps$.next(ymaps);
          this.isLoaded$.next(true);
        });
      });
  }

  private createMap(): void {
    // Добавляем карту в body для работы с апи карты
    const renderer = this.rendererFactory.createRenderer(null, null);
    const mapElement = this.document.createElement('div');
    mapElement.classList.add('d-none');

    renderer.appendChild(this.document.body, mapElement);

    this.ymaps$
      .pipe(
        take(1),
        map((ymaps: Ymaps) => {
          return new ymaps.Map(mapElement, {
            center: [0, 0],
            zoom: 1,
            controls: [],
          });
        }),
        tap((mapInstance: ymaps.Map) =>
          this.ngZone.run(() => {
            this.map$.next(mapInstance);
          }),
        ),
      )
      .subscribe();
  }

  private getScriptSource(config: YaConfig): string {
    const { enterprise, version = '2.1', ...rest } = config;
    const params = this.convertConfigIntoQueryParams(rest);

    return `https://${enterprise ? 'enterprise.' : ''}api-maps.yandex.ru/${version}/?${params}`;
  }

  private convertConfigIntoQueryParams(config: YaConfig): string {
    return Object.entries(config)
      .map(([key, value]: [string, string]) => `${key}=${value}`)
      .join('&');
  }
}
